import React, { useState } from 'react';
import { isToday } from 'date-fns';
import { GlobalCalendarEvent } from '../../types/calendar';
import CalendarEventChip from './CalendarEvent';
import CalendarEventPopover from './CalendarEventPopover';

interface MonthViewProps {
  date: Date;
  events: GlobalCalendarEvent[];
  onEventClick: (event: GlobalCalendarEvent) => void;
  onDateClick?: (date: Date) => void;
  isLoading?: boolean;
}

const MAX_VISIBLE_EVENTS = 3;
const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const MonthView: React.FC<MonthViewProps> = ({
  date,
  events,
  onEventClick,
  onDateClick,
  isLoading,
}) => {
  const [morePopover, setMorePopover] = useState<{
    day: Date;
    events: GlobalCalendarEvent[];
    rect: DOMRect;
  } | null>(null);

  const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
  const startDate = new Date(startOfMonth);
  startDate.setDate(startDate.getDate() - startOfMonth.getDay());

  const days: Date[] = [];
  const current = new Date(startDate);
  for (let i = 0; i < 42; i++) {
    days.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }

  const getEventsForDay = (day: Date): GlobalCalendarEvent[] => {
    const dayStr = day.toDateString();
    return events.filter(event => {
      const start = new Date(event.startDate);
      const end = new Date(event.endDate);
      if (event.allDay) {
        return start.toDateString() === dayStr;
      }
      return start.toDateString() === dayStr || (start <= day && end >= day);
    });
  };

  return (
    <>
      <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-lg overflow-hidden">
        {/* Header */}
        {DAYS_OF_WEEK.map(day => (
          <div key={day} className="bg-gray-50 p-3 text-center text-sm font-medium text-gray-700">
            {day}
          </div>
        ))}

        {/* Days */}
        {days.map((day, index) => {
          const dayEvents = getEventsForDay(day);
          const isCurrentMonth = day.getMonth() === date.getMonth();
          const isCurrentDay = isToday(day);

          return (
            <div
              key={index}
              className={`bg-white min-h-[120px] p-2 relative cursor-pointer hover:bg-gray-50 transition-colors ${
                !isCurrentMonth ? 'text-gray-400' : ''
              } ${isCurrentDay ? 'bg-brand-50 hover:bg-brand-100' : ''}`}
              onClick={() => onDateClick?.(day)}
              role="gridcell"
              aria-label={`${day.toLocaleDateString()}, ${dayEvents.length} events`}
            >
              {isLoading ? (
                <div className="animate-pulse">
                  <div className="h-4 w-6 bg-gray-200 rounded mb-2" />
                  <div className="h-3 w-full bg-gray-100 rounded mb-1" />
                  <div className="h-3 w-3/4 bg-gray-100 rounded" />
                </div>
              ) : (
                <>
                  <div
                    className={`text-sm font-medium mb-1 ${
                      isCurrentDay
                        ? 'text-brand-primary'
                        : isCurrentMonth
                          ? 'text-gray-900'
                          : 'text-gray-400'
                    }`}
                  >
                    {day.getDate()}
                  </div>

                  <div className="space-y-1">
                    {dayEvents.slice(0, MAX_VISIBLE_EVENTS).map(event => (
                      <CalendarEventChip
                        key={event.id}
                        event={event}
                        onClick={onEventClick}
                        compact
                      />
                    ))}
                    {dayEvents.length > MAX_VISIBLE_EVENTS && (
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          setMorePopover({
                            day,
                            events: dayEvents,
                            rect: (e.target as HTMLElement).getBoundingClientRect(),
                          });
                        }}
                        className="text-xs text-brand-primary hover:text-brand-700 px-1 py-0.5 hover:bg-brand-50 rounded transition-colors font-medium"
                      >
                        +{dayEvents.length - MAX_VISIBLE_EVENTS} more
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* More events popover */}
      {morePopover && (
        <CalendarEventPopover
          event={null}
          onClose={() => setMorePopover(null)}
          anchorRect={morePopover.rect}
        />
      )}
      {morePopover && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setMorePopover(null)} />
          <div
            className="fixed z-50 w-64 bg-white rounded-lg shadow-xl border border-gray-200 p-3 max-h-80 overflow-y-auto"
            style={{
              top: Math.min(morePopover.rect.bottom + 4, window.innerHeight - 320),
              left: Math.min(morePopover.rect.left, window.innerWidth - 270),
            }}
          >
            <div className="text-xs font-medium text-gray-500 mb-2">
              {morePopover.day.toLocaleDateString(undefined, {
                weekday: 'long',
                month: 'short',
                day: 'numeric',
              })}
            </div>
            <div className="space-y-1">
              {morePopover.events.map(event => (
                <CalendarEventChip key={event.id} event={event} onClick={onEventClick} />
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default MonthView;
